// apps/prompts/src/lib/prompt-helpers.ts
import { AI_MODEL_LABELS } from '@neuroforge/shared'
import { hubLoginUrl, hubRegisterUrl } from '@neuroforge/shared/client'
import {
  type AppIconProps,
  IconPencil,
  IconCode,
  IconTrendingUp,
  IconBolt,
  IconChartBar,
  IconPalette,
  IconBriefcase,
  IconSchool,
  IconTools,
} from '@neuroforge/ui'

/** Composant icône Tabler (compatible AppIcon) */
type TablerIcon = AppIconProps['icon']

// ─── Accent system (design tokens visuels par catégorie) ───
export interface CategoryAccent {
  color: string
  gradient: string
  icon: TablerIcon
  label: string
}

const ACCENT_MAP: Record<string, CategoryAccent> = {
  WRITING: {
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    icon: IconPencil,
    label: 'Rédaction',
  },
  CODING: {
    color: '#22c55e',
    gradient: 'linear-gradient(135deg, #22c55e, #16a34a)',
    icon: IconCode,
    label: 'Code',
  },
  MARKETING: {
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
    icon: IconTrendingUp,
    label: 'Marketing',
  },
  PRODUCTIVITY: {
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    icon: IconBolt,
    label: 'Productivité',
  },
  ANALYSIS: {
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)',
    icon: IconChartBar,
    label: 'Analyse',
  },
  CREATIVE: {
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899, #db2777)',
    icon: IconPalette,
    label: 'Créativité',
  },
  BUSINESS: {
    color: '#14b8a6',
    gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)',
    icon: IconBriefcase,
    label: 'Business',
  },
  EDUCATION: {
    color: '#f97316',
    gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
    icon: IconSchool,
    label: 'Éducation',
  },
  ADMIN_LEGAL: {
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    icon: IconSchool,
    label: 'Démarches & juridique',
  },
  REAL_ESTATE: {
    color: '#0ea5e9',
    gradient: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    icon: IconBriefcase,
    label: 'Immobilier & habitat',
  },
  CONSTRUCTION: {
    color: '#f97316',
    gradient: 'linear-gradient(135deg, #f97316, #c2410c)',
    icon: IconTools,
    label: 'Bâtiment & travaux',
  },
  EXECUTIVE_ASSISTANT: {
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    icon: IconBolt,
    label: 'Assistant de direction',
  },
  FINANCE_ACCOUNTING: {
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981, #047857)',
    icon: IconChartBar,
    label: 'Finance & compta',
  },
  HR_CAREER: {
    color: '#f43f5e',
    gradient: 'linear-gradient(135deg, #f43f5e, #be123c)',
    icon: IconSchool,
    label: 'RH & carrière',
  },
  SALES: {
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b, #b45309)',
    icon: IconTrendingUp,
    label: 'Vente',
  },
  CUSTOMER_SUPPORT: {
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #06b6d4, #0e7490)',
    icon: IconBriefcase,
    label: 'Support client',
  },
  OPERATIONS: {
    color: '#64748b',
    gradient: 'linear-gradient(135deg, #64748b, #334155)',
    icon: IconTools,
    label: 'Opérations',
  },
  PERSONAL_LIFE: {
    color: '#84cc16',
    gradient: 'linear-gradient(135deg, #84cc16, #4d7c0f)',
    icon: IconPencil,
    label: 'Vie pratique',
  },
  CUSTOM: {
    color: '#6b7280',
    gradient: 'linear-gradient(135deg, #6b7280, #4b5563)',
    icon: IconTools,
    label: 'Autre',
  },
}

const DEFAULT_ACCENT: CategoryAccent = ACCENT_MAP.CUSTOM

/** Récupère l'accent visuel pour une catégorie API (enum string) */
export function getAccent(category: string): CategoryAccent {
  return ACCENT_MAP[category] ?? DEFAULT_ACCENT
}

/** Liste toutes les catégories avec leur accent */
export function getAllCategories(): Array<{ key: string; accent: CategoryAccent }> {
  return Object.entries(ACCENT_MAP).map(([key, accent]) => ({ key, accent }))
}

/** Label lisible pour un modèle IA */
export function getAiModelLabel(model: string): string {
  const entry = AI_MODEL_LABELS[model as keyof typeof AI_MODEL_LABELS]
  return entry ? `${entry.icon} ${entry.label}` : model
}

/** Label court pour un modèle IA */
export function getAiModelName(model: string): string {
  const entry = AI_MODEL_LABELS[model as keyof typeof AI_MODEL_LABELS]
  return entry?.label ?? model
}

// ─── URLs canoniques ───

/**
 * URL canonique d'un prompt : /prompts/:id/:slug (style Stack Overflow).
 * L'id (cuid) garantit l'unicité et la résolution, le slug porte le SEO.
 * Si le slug change, PromptDetailPage redirige vers l'URL canonique (l'id fait foi).
 */
export function promptPath(prompt: { id: string; slug: string }): string {
  return `/prompts/${prompt.id}/${prompt.slug}`
}

/**
 * URL de connexion centralisée sur le Hub, avec retour vers la page courante
 * (ou `redirectTo`) après login. URL absolue cross-app : à utiliser avec
 * `<a href>` ou `window.location`, pas avec `<Link>`/`navigate`.
 */
export function loginPath(redirectTo?: string): string {
  return hubLoginUrl(redirectTo)
}

/** URL d'inscription centralisée sur le Hub. Absolue cross-app, comme loginPath. */
export function registerPath(redirectTo?: string): string {
  return hubRegisterUrl(redirectTo)
}

/** Niveau lisible */
export function getDifficultyLabel(difficulty?: number): string {
  if (!difficulty) return 'Tous niveaux'
  const labels: Record<number, string> = {
    1: 'Débutant',
    2: 'Intermédiaire',
    3: 'Avancé',
  }
  return labels[difficulty] ?? 'Tous niveaux'
}
